import React from 'react';
import {
  Box,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
  Zoom,
  IconButton,
  alpha,
  Chip,
  Divider,
  Button,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionIcon from '@mui/icons-material/Description';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BlockIcon from '@mui/icons-material/Block';
import {
  listings,
  showings,
  inspections,
  offers,
  InspectionStatus,
  OfferStatus,
} from '@prisma/client';
import StepIndicator from './StepIndicator';
import PropertyJourneyRows from './PropertyJourneyRows';
import ShowingTimeIntegrated from '../ShowingTimeIntegrated/ShowingTimeIntegrated';
import NiceModal from '@ebay/nice-modal-react';
import ScheduleTourModal from '../../pages/Dashboard/Buying/ScheduleTourModal/ScheduleTourModal';
import CreateInspectionModal from '../CreateInspectionModal/CreateInspectionModal';
import BuyerOfferModal from '../OfferModal/BuyerOfferModal';
import SignBuyerOfferModal from '../SignBuyerOfferModal/SignBuyerOfferModal';
import { useAppDispatch } from '../../store/store';
import { fetchInspections } from '../../store/slices/inspections';
import formatPhoneNumber from '../../utils/formatPhoneNumber';
import { startCase } from 'lodash';
import { CustomTooltip } from '../CustomTooltip/CustomTooltip';

export type StepStatus =
  | 'not_started'
  | 'in_progress'
  | 'completed'
  | 'optional';

export interface StepState {
  id: string;
  status: StepStatus;
  canExecute: boolean;
  label: string;
  sublabel?: string | React.ReactNode;
  actionText: string | React.ReactNode;
  icon: React.ComponentType;
  onClick?: () => void;
  disabled?: boolean;
  disabledTooltip?: string | React.ReactNode;
  showPulse?: boolean;
  showingData?: showings; // For tour step showing data
  tooltipContent?: React.ReactNode; // For additional details in tooltip
}

interface PropertyStepFlowProps {
  listing: listings;
  showing?: showings; // Single showing (backward compat for Properties.tsx)
  showings?: showings[]; // Multiple showings (preferred, from Detail.tsx)
  inspections?: inspections[];
  offer?: offers;
  onUpdate?: () => void;
  onLeaveFeedback?: () => void;
  layout?: 'steps' | 'rows';
  compact?: boolean; // Show only the primary/most relevant step
  showLabel?: boolean; // Whether to show step label in compact mode (default true)
  /** Admin customer previews expose progress without acting for the buyer. */
  readOnly?: boolean;
}

interface ConnectorLineProps {
  currentStep: StepState;
  nextStep: StepState;
  orientation?: 'horizontal' | 'vertical';
}

const ConnectorLine: React.FC<ConnectorLineProps> = ({
  currentStep,
  nextStep,
  orientation = 'horizontal',
}) => {
  const theme = useTheme();

  const getConnectorColor = () => {
    // Current step completed → success color
    if (currentStep.status === 'completed') {
      return theme.palette.success.main;
    }

    // Current step in progress → primary light color
    if (currentStep.status === 'in_progress') {
      return theme.palette.primary.light;
    }

    // Next step disabled or current not started → very subtle
    if (nextStep.disabled || currentStep.status === 'not_started') {
      return theme.palette.divider;
    }

    // Default subtle connector
    return theme.palette.grey[300];
  };

  const getConnectorStyle = () => {
    if (currentStep.status === 'completed') {
      return 'solid';
    }
    if (currentStep.status === 'in_progress') {
      return 'dashed';
    }
    return 'dotted';
  };

  const color = getConnectorColor();
  const style = getConnectorStyle();

  if (orientation === 'vertical') {
    return (
      <Box
        role="presentation"
        sx={{
          width: 2,
          height: 24,
          mx: 'auto',
          my: 1,
          backgroundColor: style === 'solid' ? color : 'transparent',
          position: 'relative',
          transition: 'all 0.3s ease',
          ...(style === 'dashed' && {
            backgroundImage: `linear-gradient(180deg, ${color} 60%, transparent 60%)`,
            backgroundSize: '2px 6px',
            backgroundRepeat: 'repeat-y',
          }),
          ...(style === 'dotted' && {
            backgroundImage: `radial-gradient(circle, ${color} 1px, transparent 1px)`,
            backgroundSize: '2px 6px',
            backgroundRepeat: 'repeat-y',
            backgroundPosition: 'center',
          }),
        }}
      />
    );
  }

  return (
    <Box
      role="presentation"
      sx={{
        flexGrow: 1,
        minWidth: 32,
        maxWidth: 80,
        height: 2,
        backgroundColor: style === 'solid' ? color : 'transparent',
        position: 'relative',
        transition: 'all 0.3s ease',
        alignSelf: 'center',
        ...(style === 'dashed' && {
          backgroundImage: `linear-gradient(90deg, ${color} 60%, transparent 60%)`,
          backgroundSize: '8px 2px',
          backgroundRepeat: 'repeat-x',
        }),
        ...(style === 'dotted' && {
          backgroundImage: `radial-gradient(circle, ${color} 1px, transparent 1px)`,
          backgroundSize: '8px 2px',
          backgroundRepeat: 'repeat-x',
          backgroundPosition: 'center',
        }),
      }}
    />
  );
};

const PropertyStepFlow: React.FC<PropertyStepFlowProps> = ({
  listing,
  showing,
  showings: showingsProp,
  inspections = [],
  offer,
  onUpdate,
  onLeaveFeedback,
  compact = false,
  layout = 'steps',
  showLabel = true,
  readOnly = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useAppDispatch();
  const showInspectionButton = true;

  // Normalize: prefer showings array, fall back to wrapping singular showing
  const allShowings = showingsProp && showingsProp.length > 0
    ? showingsProp
    : showing
      ? [showing]
      : [];

  // Helper function to select primary inspection from multiple
  const getPrimaryInspection = (inspections: inspections[]) => {
    if (inspections.length === 0) return undefined;
    if (inspections.length === 1) return inspections[0];

    // Priority order: Completed > Scheduled > Waiting Schedule > First
    const completed = inspections.find(
      (i) => i.status === InspectionStatus.COMPLETED
    );
    if (completed) return completed;

    const scheduled = inspections.find(
      (i) => i.status === InspectionStatus.SCHEDULED
    );
    if (scheduled) return scheduled;

    const waiting = inspections.find(
      (i) => i.status === InspectionStatus.WAITING_SCHEDULE
    );
    if (waiting) return waiting;

    // Fallback to first inspection
    return inspections[0];
  };

  // Helper function for contextual date formatting
  const formatInspectionDateTime = (date: Date, compact = false) => {
    const now = new Date();
    const diffDays = Math.ceil(
      (date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (compact) {
      // Medium format for sublabels to prevent wrapping
      const time = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
      });
      if (diffDays === 0) return `Today ${time}`;
      if (diffDays === 1) return `Tomorrow ${time}`;

      // Use abbreviated month format for medium display
      return (
        date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        }) +
        ', ' +
        time
      );
    }

    // Full format for tooltips and single inspections
    if (diffDays === 0)
      return `Today, ${date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
      })}`;
    if (diffDays === 1)
      return `Tomorrow, ${date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
      })}`;
    if (diffDays > 0 && diffDays < 7) {
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    }

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // Helper function for structured inspection sublabel
  const getInspectionSublabel = (
    inspection: inspections | undefined,
    totalCount = 1,
    allInspections?: inspections[],
    isCompact = false
  ) => {
    if (!inspection) return undefined;

    // For multiple inspections, show dates only
    if (totalCount > 1 && allInspections) {
      const inspectionsWithDates = allInspections
        .filter((i) => i.inspectionDate)
        .sort(
          (a, b) =>
            new Date(a.inspectionDate!).getTime() -
            new Date(b.inspectionDate!).getTime()
        )
        .slice(0, 2); // Show max 2 dates to avoid crowding

      if (inspectionsWithDates.length === 0) return undefined;

      // Compact mode: show dates horizontally separated by comma
      if (isCompact) {
        const dateStrings = inspectionsWithDates.map((insp) => {
          const date = new Date(insp.inspectionDate!);
          return formatInspectionDateTime(date, true);
        });

        const displayText =
          dateStrings.join(', ') +
          (allInspections.length > 2 ? ` +${allInspections.length - 2}` : '');

        return (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {displayText}
          </Typography>
        );
      }

      return (
        <Stack spacing={0.25} alignItems={isMobile ? 'flex-start' : 'center'}>
          {inspectionsWithDates.map((insp, idx) => {
            const date = new Date(insp.inspectionDate!);
            return (
              <Typography
                key={idx}
                variant="caption"
                color="text.secondary"
                sx={{
                  textAlign: isMobile ? 'left' : 'center',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {formatInspectionDateTime(date, true)}
              </Typography>
            );
          })}
          {allInspections.length > 2 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                textAlign: isMobile ? 'left' : 'center',
                fontStyle: 'italic',
                whiteSpace: 'nowrap',
              }}
            >
              +{allInspections.length - 2} more
            </Typography>
          )}
        </Stack>
      );
    }

    const inspectionDate = inspection.inspectionDate
      ? new Date(inspection.inspectionDate)
      : null;

    // For single inspection, show date only
    // In compact mode, show horizontally
    if (isCompact) {
      if (inspectionDate) {
        return (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {formatInspectionDateTime(inspectionDate, true)}
          </Typography>
        );
      }
      return undefined;
    }

    // For regular mode, show date only (no vendor name)
    if (inspectionDate) {
      return (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            textAlign: isMobile ? 'left' : 'center',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {formatInspectionDateTime(inspectionDate)}
        </Typography>
      );
    }
    return undefined;
  };

  // Helper function to create detailed inspection tooltip
  const getInspectionTooltipContent = (inspection: inspections | undefined) => {
    if (!inspection) return null;

    const inspectionInfo = [];

    // Always include vendor name (or indicate if not available)
    inspectionInfo.push(
      <Typography key="vendor" variant="body2">
        <strong>Inspector:</strong> {inspection.vendorName || 'Not specified'}
      </Typography>
    );

    // Include inspection type
    if (inspection.type) {
      inspectionInfo.push(
        <Typography key="type" variant="body2">
          <strong>Type:</strong> {startCase(inspection.type.toLowerCase())}
        </Typography>
      );
    }

    // Always include vendor phone (or indicate if not available)
    inspectionInfo.push(
      <Typography key="phone" variant="body2">
        <strong>Phone:</strong>{' '}
        {inspection.vendorPhone
          ? formatPhoneNumber(inspection.vendorPhone)
          : 'Not provided'}
      </Typography>
    );

    // Include cost if available
    if (inspection.cost) {
      inspectionInfo.push(
        <Typography key="cost" variant="body2">
          <strong>Cost:</strong> ${inspection.cost}
        </Typography>
      );
    }

    // Include date if available
    if (inspection.inspectionDate) {
      const date = new Date(inspection.inspectionDate);
      inspectionInfo.push(
        <Typography key="date" variant="body2">
          <strong>Scheduled:</strong> {formatInspectionDateTime(date)}
        </Typography>
      );
    }

    return (
      <Box sx={{ minWidth: 200 }}>
        <Stack spacing={0.5}>{inspectionInfo}</Stack>
      </Box>
    );
  };

  // Helper function for multiple inspections tooltip
  const getMultiInspectionTooltipContent = (allInspections: inspections[]) => {
    if (allInspections.length === 0) return null;
    if (allInspections.length === 1)
      return getInspectionTooltipContent(allInspections[0]);

    return (
      <Box sx={{ minWidth: 250, maxWidth: 350 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          {allInspections.length} Inspections
        </Typography>
        <Stack spacing={1.5}>
          {allInspections.map((inspection, index) => {
            const inspectionDate = inspection.inspectionDate
              ? new Date(inspection.inspectionDate)
              : null;

            return (
              <Box key={inspection.id || index}>
                <Stack spacing={0.25}>
                  <Typography variant="body2" fontWeight={600}>
                    {inspection.vendorName}
                  </Typography>
                  {inspectionDate && (
                    <Typography variant="caption" color="text.secondary">
                      {formatInspectionDateTime(inspectionDate)}
                    </Typography>
                  )}
                  <Stack direction="row" spacing={2} flexWrap="wrap">
                    {inspection.type && (
                      <Typography variant="caption">
                        {startCase(inspection.type.toLowerCase())}
                      </Typography>
                    )}
                    {inspection.vendorPhone && (
                      <Typography variant="caption">
                        {formatPhoneNumber(inspection.vendorPhone)}
                      </Typography>
                    )}
                    {inspection.cost && (
                      <Typography variant="caption" fontWeight={600}>
                        ${inspection.cost}
                      </Typography>
                    )}
                  </Stack>
                </Stack>
                {index < allInspections.length - 1 && (
                  <Divider sx={{ mt: 1.5 }} />
                )}
              </Box>
            );
          })}
        </Stack>
      </Box>
    );
  };

  // Helper function to get inspection types for display
  const getInspectionTypesDisplay = (
    allInspections: inspections[],
    maxTypes = 2
  ): string => {
    if (allInspections.length === 0) return '';
    if (allInspections.length === 1 && allInspections[0].type) {
      return startCase(allInspections[0].type.toLowerCase());
    }

    const uniqueTypes = Array.from(
      new Set(
        allInspections
          .filter((i) => i.type)
          .map((i) => startCase(i.type!.toLowerCase()))
      )
    );

    if (uniqueTypes.length === 0) return '';
    if (uniqueTypes.length <= maxTypes) {
      return uniqueTypes.join(', ');
    }

    return `${uniqueTypes.slice(0, maxTypes).join(', ')} +${
      uniqueTypes.length - maxTypes
    }`;
  };

  // Determine Tour Step State
  const getTourStepState = (): StepState => {
    const openScheduleModal = () => {
      NiceModal.show(ScheduleTourModal, {
        listing: listing,
        numDays: 5,
      });
    };

    if (allShowings.length === 0) {
      return {
        id: 'tour',
        status: 'not_started',
        canExecute: true,
        label: 'Tour',
        actionText: 'Schedule',
        icon: EventIcon,
        onClick: openScheduleModal,
      };
    }

    const now = new Date();
    const completedTours = allShowings.filter((s) => {
      const d = s.proposedTime ? new Date(s.proposedTime) : null;
      return d && d < now && s.accepted;
    });
    const activeTour = allShowings.find((s) => {
      if (s.accepted === null || s.cancelledAt) return false; // cancelled or declined
      const d = s.proposedTime ? new Date(s.proposedTime) : null;
      return d && d >= now;
    });
    const latestShowing = allShowings[0]; // sorted desc by proposedTime

    // All cancelled or declined, none completed → reschedule
    if (completedTours.length === 0 && !activeTour) {
      if (latestShowing.accepted === null || latestShowing.cancelledAt) {
        return {
          id: 'tour',
          status: 'not_started',
          canExecute: true,
          label: 'Tour Cancelled',
          sublabel: 'Reschedule',
          actionText: 'Reschedule',
          icon: EventIcon,
          onClick: openScheduleModal,
          showingData: latestShowing,
        };
      }
    }

    // Has an active (future) tour, none completed yet
    if (activeTour && completedTours.length === 0) {
      return {
        id: 'tour',
        status: 'in_progress',
        canExecute: false,
        label: activeTour.accepted ? 'Tour Confirmed' : 'Tour Pending',
        actionText: 'Scheduled',
        icon: EventIcon,
        showingData: activeTour,
      };
    }

    // At least one completed tour
    if (completedTours.length > 0) {
      const mostRecentCompleted = completedTours[0];
      const completedDate = new Date(mostRecentCompleted.proposedTime);

      // Has completed + active future tour
      if (activeTour) {
        const nextDate = new Date(activeTour.proposedTime);
        return {
          id: 'tour',
          status: 'completed',
          canExecute: true,
          label: 'Tour Completed',
          sublabel: `Next tour: ${nextDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${nextDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`,
          actionText: (
            <Button
              variant="outlined"
              color="success"
              size="small"
              onClick={(e) => { e.stopPropagation(); onLeaveFeedback?.(); }}
            >
              Leave Feedback
            </Button>
          ),
          icon: CheckCircleIcon,
        };
      }

      // All completed, no active tour → show "Leave Feedback" + "Tour Again"
      return {
        id: 'tour',
        status: 'completed',
        canExecute: true,
        label: 'Tour Completed',
        sublabel: completedDate.toLocaleDateString(),
        actionText: (
          <Stack
            direction={layout === 'rows' ? 'row' : 'column'}
            sx={{ gap: 1, flexWrap: 'wrap', mt: layout === 'rows' ? 0 : 0.5 }}
          >
            <Button
              variant="outlined"
              color="success"
              size="small"
              onClick={(e) => { e.stopPropagation(); onLeaveFeedback?.(); }}
            >
              Leave Feedback
            </Button>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<EventIcon />}
              onClick={(e) => { e.stopPropagation(); openScheduleModal(); }}
            >
              Tour Again
            </Button>
          </Stack>
        ),
        icon: CheckCircleIcon,
      };
    }

    // Fallback: treat latest showing as the active one
    return {
      id: 'tour',
      status: 'in_progress',
      canExecute: false,
      label: latestShowing.accepted ? 'Tour Confirmed' : 'Tour Pending',
      actionText: 'Scheduled',
      icon: EventIcon,
      showingData: latestShowing,
    };
  };

  // An offer out for review, signature or a seller response locks new
  // inspections until the seller accepts (due diligence) or declines.
  const IN_FLIGHT_OFFER_STATUSES: OfferStatus[] = [
    OfferStatus.PENDING,
    OfferStatus.REVIEW,
    OfferStatus.REVIEWED,
    OfferStatus.SIGNED,
    OfferStatus.COUNTERED,
  ];
  const isOfferInFlight =
    !!offer && IN_FLIGHT_OFFER_STATUSES.includes(offer.status);

  const offerLockedInspectionStep: StepState = {
    id: 'inspection',
    status: 'not_started',
    canExecute: false,
    label: 'Inspection',
    sublabel: 'Available once your offer is accepted',
    actionText: 'Unavailable',
    icon: SearchIcon,
    disabled: true,
    disabledTooltip:
      'Your offer is in progress. Inspections are scheduled during the due diligence period after the seller accepts.',
  };

  // Determine Inspection Step State
  const getInspectionStepState = (): StepState => {
    if (!showInspectionButton) {
      return {
        id: 'inspection',
        status: 'optional',
        canExecute: false,
        label: 'Inspection',
        sublabel: 'Not Available',
        actionText: 'N/A',
        icon: SearchIcon,
        disabled: true,
      };
    }

    if (!inspections || inspections.length === 0) {
      if (isOfferInFlight) {
        return offerLockedInspectionStep;
      }
      return {
        id: 'inspection',
        status: 'not_started',
        canExecute: true,
        label: 'Inspection',
        sublabel: 'Optional',
        actionText: 'Schedule',
        icon: SearchIcon,
        onClick: () => {
          NiceModal.show(CreateInspectionModal, {
            listing: listing,
            onSuccess: () => {
              dispatch(fetchInspections());
              onUpdate?.();
            },
          });
        },
      };
    }

    // Get the primary inspection and counts
    const primaryInspection = getPrimaryInspection(inspections);
    const totalCount = inspections.length;

    // Count inspections by status
    const completedCount = inspections.filter(
      (i) => i.status === InspectionStatus.COMPLETED
    ).length;
    const scheduledCount = inspections.filter(
      (i) => i.status === InspectionStatus.SCHEDULED
    ).length;
    const waitingCount = inspections.filter(
      (i) => i.status === InspectionStatus.WAITING_SCHEDULE
    ).length;

    const hasCompleted = completedCount > 0;
    const hasScheduled = scheduledCount > 0;
    const hasWaiting = waitingCount > 0;

    if (hasCompleted) {
      const completedInspection = inspections.find(
        (i) => i.status === InspectionStatus.COMPLETED
      );
      const completedInspections = inspections.filter(
        (i) => i.status === InspectionStatus.COMPLETED
      );

      return {
        id: 'inspection',
        status: 'completed',
        canExecute: true,
        label:
          completedCount === totalCount && totalCount > 1
            ? 'Inspections Complete'
            : 'Inspection Complete',
        sublabel: getInspectionSublabel(
          completedInspection,
          totalCount,
          inspections
        ),
        actionText: (
          <Button
            variant="outlined"
            color="success"
            size="small"
            startIcon={<DescriptionIcon sx={{ fontSize: 14 }} />}
            onClick={(e) => {
              e.stopPropagation();
              if (completedInspection?.reportUrl) {
                window.open(completedInspection.reportUrl, '_blank');
              }
            }}
            sx={{
              mt: 0.5,
              fontSize: '0.7rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              py: 0.25,
              px: 1,
              minHeight: 28,
            }}
          >
            {totalCount > 1
              ? `View ${completedCount} Report${
                  completedCount > 1 ? 's' : ''
                }`
              : 'View Report'}
          </Button>
        ),
        icon: DescriptionIcon,
        onClick: () => {
          if (completedInspection?.reportUrl) {
            window.open(completedInspection.reportUrl, '_blank');
          }
        },
      };
    }

    if (hasScheduled || hasWaiting) {
      const scheduledInspection = inspections.find(
        (i) =>
          i.status === InspectionStatus.SCHEDULED ||
          i.status === InspectionStatus.WAITING_SCHEDULE
      );

      const activeCount = scheduledCount + waitingCount;
      const activeInspections = inspections.filter(
        (i) =>
          i.status === InspectionStatus.SCHEDULED ||
          i.status === InspectionStatus.WAITING_SCHEDULE
      );
      const label =
        activeCount > 1
          ? `${activeCount} Inspections ${
              hasWaiting && !hasScheduled ? 'Pending' : 'Scheduled'
            }`
          : scheduledInspection?.status === InspectionStatus.WAITING_SCHEDULE
          ? 'Scheduling Inspection'
          : 'Inspection Scheduled';

      return {
        id: 'inspection',
        status: 'in_progress',
        canExecute: false,
        label: label,
        sublabel: getInspectionSublabel(
          scheduledInspection,
          totalCount,
          inspections
        ),
        actionText: (
          <CustomTooltip
            title={
              totalCount === 1
                ? getInspectionTooltipContent(scheduledInspection)
                : getMultiInspectionTooltipContent(activeInspections)
            }
          >
            <Box
              component="span"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
            >
              <Typography
                variant="caption"
                color="primary"
                sx={{
                  fontWeight: 600,
                  textTransform: totalCount === 1 ? 'uppercase' : 'none',
                  letterSpacing: totalCount === 1 ? 0.5 : 0,
                }}
              >
                {totalCount === 1
                  ? 'Scheduled'
                  : getInspectionTypesDisplay(activeInspections) || 'Scheduled'}
              </Typography>
              <InfoOutlinedIcon
                sx={{ fontSize: 14, color: 'action.active', opacity: 0.6 }}
              />
            </Box>
          </CustomTooltip>
        ),
        icon: SearchIcon,
      };
    }

    if (isOfferInFlight) {
      return offerLockedInspectionStep;
    }

    return {
      id: 'inspection',
      status: 'not_started',
      canExecute: true,
      label: 'Schedule Inspection',
      actionText: 'Schedule',
      icon: SearchIcon,
      onClick: () => {
        NiceModal.show(CreateInspectionModal, {
          listing: listing,
          onSuccess: () => {
            dispatch(fetchInspections());
            onUpdate?.();
          },
        });
      },
    };
  };

  // Determine Offer Step State
  const getOfferStepState = (): StepState => {
    if (!offer) {
      return {
        id: 'offer',
        status: 'not_started',
        canExecute: true,
        label: 'Offer',
        sublabel: 'Ready to draft',
        actionText: 'Draft offer',
        icon: LocalOfferIcon,
        onClick: () => {
          NiceModal.show(BuyerOfferModal, {
            listing: listing,
          });
        },
      };
    }

    const isOfferReviewed = offer.status === OfferStatus.REVIEWED;
    const isOfferSigned = offer.status === OfferStatus.SIGNED;
    const isOfferAccepted = offer.status === OfferStatus.ACCEPTED;
    const isOfferDeclined = offer.status === OfferStatus.DECLINED;
    const isOfferRescinded = offer.status === OfferStatus.RESCINDED;
    const isOfferCountered = offer.status === OfferStatus.COUNTERED;

    if (isOfferSigned) {
      return {
        id: 'offer',
        status: 'completed',
        canExecute: false,
        label: 'Offer Signed',
        sublabel: 'Awaiting response',
        actionText: 'Signed',
        icon: CheckCircleIcon,
      };
    }

    if (isOfferAccepted) {
      return {
        id: 'offer',
        status: 'completed',
        canExecute: false,
        label: 'Offer Accepted',
        sublabel: 'Congratulations!',
        actionText: 'Accepted',
        icon: CheckCircleIcon,
      };
    }

    if (isOfferDeclined) {
      return {
        id: 'offer',
        status: 'not_started',
        canExecute: true,
        label: 'Offer Declined',
        sublabel: 'Create new offer',
        actionText: 'New Offer',
        icon: LocalOfferIcon,
        onClick: () => {
          NiceModal.show(BuyerOfferModal, {
            listing: listing,
          });
        },
      };
    }

    if (isOfferRescinded) {
      return {
        id: 'offer',
        status: 'not_started',
        canExecute: true,
        label: 'Offer Rescinded',
        sublabel: 'Create new offer',
        actionText: 'New Offer',
        icon: LocalOfferIcon,
        onClick: () => {
          NiceModal.show(BuyerOfferModal, {
            listing: listing,
          });
        },
      };
    }

    if (isOfferCountered) {
      return {
        id: 'offer',
        status: 'in_progress',
        canExecute: false,
        label: 'Seller Countered',
        sublabel: 'Review the counter offer',
        actionText: 'Countered',
        icon: LocalOfferIcon,
      };
    }

    if (isOfferReviewed) {
      return {
        id: 'offer',
        status: 'in_progress',
        canExecute: true,
        label: 'Ready to Sign',
        actionText: 'Review & Sign',
        icon: EditIcon,
        onClick: () => {
          NiceModal.show(SignBuyerOfferModal, {
            offer: offer,
            listing: listing,
          });
        },
      };
    }

    // PENDING / REVIEW: submitted and under review, so the buyer's step is done
    return {
      id: 'offer',
      status: 'completed',
      canExecute: false,
      label: 'Offer Submitted',
      sublabel: 'Under review',
      actionText: 'Pending',
      icon: CheckCircleIcon,
    };
  };

  const allSteps: StepState[] = [
    getTourStepState(),
    getInspectionStepState(),
    getOfferStepState(),
  ];

  // Determine which steps to show
  let steps = readOnly
    ? allSteps.map((step) => ({
        ...step,
        onClick: undefined,
        disabled: true,
        actionText:
          typeof step.actionText === 'string' ? step.actionText : 'Preview only',
      }))
    : allSteps;

  if (compact) {
    // In compact mode, show only the most relevant step
    // Priority logic:
    // 1. If an offer is in flight (submitted, in review, signed, countered), show offer
    // 2. If offer is accepted and inspection not scheduled, show inspection
    // 3. If inspections are scheduled, show inspection
    // 4. Otherwise follow normal flow (first actionable step)

    const offerStep = steps.find((s) => s.id === 'offer');
    const inspectionStep = steps.find((s) => s.id === 'inspection');

    let primaryStep: StepState | undefined;

    // Check if an offer is in flight
    if (
      offer &&
      IN_FLIGHT_OFFER_STATUSES.includes(offer.status)
    ) {
      primaryStep = offerStep;
    }
    // Check if offer is accepted and inspection not scheduled
    else if (
      offer &&
      offer.status === OfferStatus.ACCEPTED &&
      inspectionStep &&
      inspectionStep.status === 'not_started'
    ) {
      primaryStep = inspectionStep;
    }
    // Check if inspections are scheduled or in progress
    else if (inspectionStep && inspectionStep.status === 'in_progress') {
      primaryStep = inspectionStep;
    }
    // Default priority: first actionable step
    else {
      primaryStep =
        steps.find(
          (s) => s.status === 'not_started' && s.canExecute && !s.disabled
        ) ||
        steps.find((s) => s.status === 'in_progress') ||
        steps.find((s) => s.status === 'not_started') ||
        steps[0];
    }

    // Ensure we always have a primary step (fallback to first step)
    steps = [primaryStep || steps[0]];
  }

  // Calculate overall progress
  const completedSteps = allSteps.filter(
    (s) => s.status === 'completed'
  ).length;
  const totalSteps = allSteps.filter((s) => s.status !== 'optional').length;
  const progressPercentage =
    totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;

  if (layout === 'rows' && !compact) {
    return <PropertyJourneyRows steps={steps} onUpdate={onUpdate} />;
  }

  if (isMobile && !compact) {
    // Regular vertical layout for mobile (non-compact mode only)
    return (
      <Box sx={{ width: '100%', py: 2 }}>
        <Stack spacing={0}>
          {steps.map((step, index) => {
            const isClickable = step.onClick && !step.disabled;
            const stepContent = (
              <Stack direction="row" alignItems="stretch" spacing={0}>
                {/* Step indicator on the left */}
                <Stack alignItems="center" sx={{ width: 60 }}>
                  <StepIndicator
                    status={step.status}
                    icon={step.icon}
                    size="medium"
                    onClick={step.onClick}
                    disabled={step.disabled}
                  />
                  {!compact && index < steps.length - 1 && (
                    <ConnectorLine
                      currentStep={step}
                      nextStep={steps[index + 1]}
                      orientation="vertical"
                    />
                  )}
                </Stack>

                {/* Step content on the right */}
                <Box
                  sx={{
                    flex: 1,
                    pb: index < steps.length - 1 ? 2 : 0,
                    cursor: isClickable ? 'pointer' : 'default',
                    '&:hover': isClickable
                      ? {
                          '& .step-label': {
                            color: 'primary.main',
                          },
                        }
                      : {},
                  }}
                  onClick={isClickable ? step.onClick : undefined}
                >
                  <Typography
                    className="step-label"
                    variant="body1"
                    fontWeight={600}
                    color={
                      step.status === 'completed'
                        ? 'success.main'
                        : 'text.primary'
                    }
                    sx={{
                      whiteSpace:
                        step.id === 'inspection' &&
                        step.label.includes('Inspection')
                          ? 'pre-line'
                          : 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {step.id === 'inspection' &&
                    step.label === 'Inspection Scheduled'
                      ? 'Inspection\nScheduled'
                      : step.id === 'inspection' &&
                        step.label === 'Scheduling Inspection'
                      ? 'Scheduling\nInspection'
                      : step.label}
                  </Typography>
                  {step.sublabel &&
                    !step.showingData &&
                    (typeof step.sublabel === 'string' ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {step.sublabel}
                      </Typography>
                    ) : (
                      <Box>{step.sublabel}</Box>
                    ))}
                  {!step.showingData &&
                    (typeof step.actionText === 'string' ? (
                      <Typography
                        variant="caption"
                        color={
                          step.status === 'completed'
                            ? 'success.main'
                            : 'primary.main'
                        }
                        sx={{
                          fontWeight: 500,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {step.actionText}
                      </Typography>
                    ) : (
                      <Box>{step.actionText}</Box>
                    ))}

                  {/* Show ShowingTimeIntegrated for tour step if showing data exists */}
                  {step.id === 'tour' && step.showingData && (
                    <Box sx={{ mt: 1, width: '100%' }}>
                      <ShowingTimeIntegrated
                        showing={step.showingData}
                        onUpdate={onUpdate}
                      />
                    </Box>
                  )}
                </Box>
              </Stack>
            );

            return (
              <Box key={step.id}>
                {step.disabled && step.disabledTooltip ? (
                  <CustomTooltip title={step.disabledTooltip} placement="right">
                    <Box>{stepContent}</Box>
                  </CustomTooltip>
                ) : step.tooltipContent ? (
                  <CustomTooltip title={step.tooltipContent} placement="right">
                    <Box>{stepContent}</Box>
                  </CustomTooltip>
                ) : (
                  stepContent
                )}
              </Box>
            );
          })}
        </Stack>
      </Box>
    );
  }

  // Horizontal layout for both regular and compact modes
  return (
    <Box sx={{ width: '100%' }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent={compact ? 'flex-end' : 'space-between'}
        spacing={0}
      >
        {steps.map((step, index) => {
          const isClickable = step.onClick && !step.disabled;

          // Compact horizontal layout
          if (compact) {
            const stepContent = (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 1.5,
                  py: 1,
                  borderRadius: 2,
                  cursor: isClickable ? 'pointer' : 'default',
                  transition: 'all 0.2s ease',
                  '&:hover': isClickable
                    ? {
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                        '& .step-indicator': {
                          transform: 'scale(1.05)',
                        },
                      }
                    : {},
                }}
                onClick={isClickable ? step.onClick : undefined}
              >
                {/* Icon on the left */}
                <Box sx={{ flexShrink: 0 }}>
                  <StepIndicator
                    status={step.status}
                    icon={step.icon}
                    size="small"
                    onClick={undefined} // Handle click at parent level
                    disabled={step.disabled}
                  />
                </Box>

                {/* Text content on the right */}
                <Stack
                  spacing={0.25}
                  alignItems="flex-start"
                  sx={{ minWidth: 0, flex: 1 }}
                >
                  {showLabel && (
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color={
                        step.status === 'completed'
                          ? 'success.main'
                          : 'text.primary'
                      }
                      sx={{
                        lineHeight: 1.2,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '100%',
                      }}
                    >
                      {step.label}
                    </Typography>
                  )}

                  {/* Compact sublabel and action text on same line when possible */}
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ minWidth: 0, width: '100%' }}
                  >
                    {step.sublabel && !step.showingData && (
                      <Box
                        sx={{ minWidth: 0, flex: step.actionText ? 1 : 'none' }}
                      >
                        {typeof step.sublabel === 'string' ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              lineHeight: 1.2,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {step.sublabel}
                          </Typography>
                        ) : // For complex sublabels (like inspection dates), render them compactly
                        step.id === 'inspection' &&
                          React.isValidElement(step.sublabel) ? (
                          // Override inspection sublabel for compact mode
                          (() => {
                            const primaryInspection =
                              getPrimaryInspection(inspections);
                            const totalCount = inspections.length;
                            return getInspectionSublabel(
                              primaryInspection,
                              totalCount,
                              inspections,
                              true
                            );
                          })()
                        ) : (
                          step.sublabel
                        )}
                      </Box>
                    )}

                    {!step.showingData && step.actionText && (
                      <Box sx={{ flexShrink: 0 }}>
                        {typeof step.actionText === 'string' ? (
                          <Typography
                            variant="caption"
                            color={
                              step.status === 'completed'
                                ? 'success.main'
                                : 'primary.main'
                            }
                            sx={{
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: 0.5,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {step.actionText}
                          </Typography>
                        ) : (
                          step.actionText
                        )}
                      </Box>
                    )}
                  </Stack>

                  {/* Show ShowingTimeIntegrated for tour step if showing data exists */}
                  {step.id === 'tour' && step.showingData && (
                    <Box sx={{ mt: 0.5, width: '100%' }}>
                      <ShowingTimeIntegrated
                        showing={step.showingData}
                        onUpdate={onUpdate}
                      />
                    </Box>
                  )}
                </Stack>
              </Box>
            );

            return (
              <React.Fragment key={step.id}>
                {step.disabled && step.disabledTooltip ? (
                  <CustomTooltip title={step.disabledTooltip}>
                    <Box>{stepContent}</Box>
                  </CustomTooltip>
                ) : step.tooltipContent ? (
                  <CustomTooltip title={step.tooltipContent}>
                    <Box>{stepContent}</Box>
                  </CustomTooltip>
                ) : (
                  stepContent
                )}
              </React.Fragment>
            );
          }

          // Regular vertical layout for non-compact mode
          const stepContent = (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                flex: '1 1 0',
                minWidth: 100,
                maxWidth: 180,
                px: 1.5,
                py: 2,
                borderRadius: 2,
                cursor: isClickable ? 'pointer' : 'default',
                transition: 'all 0.2s ease',
                '&:hover': isClickable
                  ? {
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                      transform: 'translateY(-2px)',
                      '& .step-indicator': {
                        transform: 'scale(1.1)',
                      },
                    }
                  : {},
              }}
              onClick={isClickable ? step.onClick : undefined}
            >
              <StepIndicator
                status={step.status}
                icon={step.icon}
                size="large"
                onClick={undefined} // Handle click at parent level
                disabled={step.disabled}
              />
              <Stack alignItems="center" spacing={0.5} sx={{ mt: 1 }}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  color={
                    step.status === 'completed'
                      ? 'success.main'
                      : 'text.primary'
                  }
                  sx={{
                    textAlign: 'center',
                    whiteSpace:
                      step.id === 'inspection' &&
                      step.label.includes('Inspection')
                        ? 'pre-line'
                        : 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {step.id === 'inspection' &&
                  step.label === 'Inspection Scheduled'
                    ? 'Inspection\nScheduled'
                    : step.id === 'inspection' &&
                      step.label === 'Scheduling Inspection'
                    ? 'Scheduling\nInspection'
                    : step.label}
                </Typography>
                {step.sublabel &&
                  !step.showingData &&
                  (typeof step.sublabel === 'string' ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {step.sublabel}
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      {step.sublabel}
                    </Box>
                  ))}
                {!step.showingData &&
                  (typeof step.actionText === 'string' ? (
                    <Typography
                      variant="caption"
                      color={
                        step.status === 'completed'
                          ? 'success.main'
                          : 'primary.main'
                      }
                      sx={{
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {step.actionText}
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      {step.actionText}
                    </Box>
                  ))}

                {/* Show ShowingTimeIntegrated for tour step if showing data exists */}
                {step.id === 'tour' && step.showingData && (
                  <Box sx={{ mt: 1, minWidth: 200 }}>
                    <ShowingTimeIntegrated
                      showing={step.showingData}
                      onUpdate={onUpdate}
                    />
                  </Box>
                )}
              </Stack>
            </Box>
          );

          return (
            <React.Fragment key={step.id}>
              {step.disabled && step.disabledTooltip ? (
                <CustomTooltip title={step.disabledTooltip}>
                  <Box>{stepContent}</Box>
                </CustomTooltip>
              ) : step.tooltipContent ? (
                <CustomTooltip title={step.tooltipContent}>
                  <Box>{stepContent}</Box>
                </CustomTooltip>
              ) : (
                stepContent
              )}

              {!compact && index < steps.length - 1 && (
                <ConnectorLine
                  currentStep={step}
                  nextStep={steps[index + 1]}
                  orientation="horizontal"
                />
              )}
            </React.Fragment>
          );
        })}
      </Stack>
    </Box>
  );
};

export default PropertyStepFlow;
